import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { H3, Text, Tooltip } from 'components';
import TipCalculatorSiteSelector from 'components/SiteSelector/TipCalculatorSiteSelector';
import { appConfig } from 'config/app';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { DaySegments, getDaySegmentsRequest, selectDaySegments } from 'store/Collections/DaySegments';
import { selectCurrentEmployeeSite } from 'store/Employers';
import { IStore } from 'store/types';
import { PermissionType } from 'types';
import { hasSitePermission } from 'utils/employers';
import { SectionTitle, SettingsContent, SettingsContentLayout, SettingsPageHeader, SettingsSection, SettingsSectionContent, SettingsSectionHeader } from '../../Settings/components';
import { AddButton, DisabledWrapper, EnabledWrapper, LoadingSkeleton, StyledLink } from '../styles';
import { DaySegmentForm, DaySegmentItem } from './components';
import { SuggestDaySegments } from './components/SuggestDaySegments';

const DaySegmentsSettings = () => {
  const dispatch = useDispatch();
  const [addFormOpen, setAddFormOpen] = useState(false);
  const [editDaySegmentId, setEditDaySegmentId] = useState<string | null>(null);
  const currentSite = useSelector(selectCurrentEmployeeSite);
  const daySegmentsForSite = useSelector(selectDaySegments());
  const isLoadingDaySegments = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, DaySegments.GET_DAY_SEGMENTS_REQUEST));

  const addPermission = hasSitePermission(currentSite?.permissions, PermissionType.SITE_DAYSEGMENT_CREATE);
  const editPermission = hasSitePermission(currentSite?.permissions, PermissionType.SITE_DAYSEGMENT_UPDATE);
  const deletePermission = hasSitePermission(currentSite?.permissions, PermissionType.SITE_DAYSEGMENT_DELETE);

  useEffect(() => {
    if (currentSite?.id && currentSite?.settings?.tipCalculator) {
      dispatch(getDaySegmentsRequest({ siteId: currentSite.id, options: { sort: 'created_at', limit: 0 } }));
    }
    setAddFormOpen(false);
  }, [currentSite]);

  const mappedDaySegments = useMemo(
    () =>
      daySegmentsForSite?.data.map((daySegment) =>
        editDaySegmentId === daySegment.id ? (
          <DaySegmentForm key={daySegment.id} daySegment={daySegment} siteId={currentSite.id} onClose={() => setEditDaySegmentId(null)} />
        ) : (
          <DaySegmentItem key={daySegment.id} daySegment={daySegment} editPermission={editPermission} deletePermission={deletePermission} onEdit={() => setEditDaySegmentId(daySegment.id)} disabled={addFormOpen || !!editDaySegmentId} />
        )
      ),
    [daySegmentsForSite, currentSite, editDaySegmentId, addFormOpen, editPermission, deletePermission]
  );

  return (
    <SettingsContent data-cy="day-segments-settings-container">
      <SettingsPageHeader />

      <SettingsContentLayout>
        <SettingsSection>
          <SettingsSectionHeader>
            <H3>Day Segments</H3>
          </SettingsSectionHeader>

          <SettingsSectionContent light data-cy="day-segments-settings-form">
            <SectionTitle>Select Site</SectionTitle>
            <TipCalculatorSiteSelector />

            {!currentSite && <LoadingSkeleton />}

            {currentSite &&
              (currentSite.settings?.tipCalculator ? (
                <>
                  <EnabledWrapper>
                    <Text size="small">By default, tips will be paid by business day. If you need tips to be calculated more granularly, setup day segments here.</Text>
                  </EnabledWrapper>

                  {isLoadingDaySegments ? <LoadingSkeleton /> : mappedDaySegments}

                  {addFormOpen && <DaySegmentForm siteId={currentSite.id} onClose={() => setAddFormOpen(false)} />}

                  <AddButton
                    appearance="white"
                    icon="Plus"
                    label="Add Day Segment"
                    className="day-segment-add-button"
                    onClick={() => setAddFormOpen(true)}
                    disabled={addFormOpen || !!editDaySegmentId || !addPermission}
                    tooltipId="no-add-permission"
                  />
                  {!addPermission && (
                    <Tooltip id="no-add-permission">
                      You do not have permission to add day segments.
                      <br />
                      Contact your supervisor for more information.
                    </Tooltip>
                  )}
                </>
              ) : (
                <DisabledWrapper>
                  <Text fontWeight="semibold">Tip Calculations Not Enabled</Text>
                  <Text size="small">
                    {'The site selected is not enabled for tip calculations. '}
                    <StyledLink target="_blank" rel="noopener noreferrer" href={appConfig.LINKS.SUPPORT}>
                      Contact Kickfin Support
                    </StyledLink>
                    {' to learn more.'}
                  </Text>
                </DisabledWrapper>
              ))}
          </SettingsSectionContent>
          <SuggestDaySegments />
        </SettingsSection>
      </SettingsContentLayout>
    </SettingsContent>
  );
};

export default DaySegmentsSettings;
